import { Ear, FlaskConical, Image as ImageIcon, Pause, Play, Sigma, SkipBack, SkipForward, Square, Volume2, X } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ChipEvalIcon } from './eval-chip';
import { spokenFeedbackPreface } from './fired-feedback';
import {
  screenReaderOutline,
  type ControlRole, type ControlState, type OutlineRow,
} from './preview-a11y-scan';

import { Switch } from '@/components/ui/switch';

/**
 * The findings side of Preview's accessibility review.
 *
 * A REPORT, not a transform: it needs space to read and a way to get from a finding to the thing it
 * is about. On desktop that is the right-hand panel, free in Preview because both authoring panels
 * close on entry. On phone it is a full-screen sheet, so review is sequential rather than
 * side-by-side — each row scrolls to its element and flashes it, since you cannot see both at once.
 *
 * Scans when the previewed task re-renders, keyed on `scanKey`, and never in between: a scan reads
 * computed styles for every authored node, and nothing in Preview drifts on its own.
 */

/** Briefly ring the element a finding is about, and bring it into view. The ring is inline rather
 * than a class so it cannot be defeated by the block's own styles, and it self-clears — a
 * highlight that outlives the glance becomes part of the design. */
/** Guarded because a handler that throws must never break what called it: playback fires this
 * from an utterance's `onstart`, and an exception there abandoned the rest of the queue. */
function bringIntoView(el: Element) {
  if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/**
 * What to draw the ring on, which is not always the thing being announced.
 *
 * A composite field puts its border on a WRAPPER and the control inside it is borderless. Ringing
 * the control then drew the ring INSIDE the box — reported for Numerical's answer — and the unit
 * field was worse: its bordered container is `overflow-hidden`, so an outline on the combobox or on
 * the unit-symbol button beside it was clipped to a single edge, showing as a stray line between
 * the two. Keyword looked right the whole time only because its input IS its bordered box.
 *
 * `data-field-box` marks the box, the same way `data-field-id` already marks it for the undo ring —
 * `number-field.tsx` learned this exact lesson and the marker is the same idea, made explicit so a
 * new composite field opts in rather than inheriting a guess about the DOM.
 */
function ringTarget(el: Element): HTMLElement {
  return (el.closest?.('[data-field-box]') ?? el) as HTMLElement;
}

/** The shared "here, this is the one" ring. See `agaviPulse` in `globals.css`. */
const PULSE_CLASS = 'agavi-pulse';
const PULSE_MS = 700;

/**
 * Exported, because the submission summary's Fix needs the identical gesture.
 *
 * A second copy would drift on the one detail that matters — the ring outliving the glance,
 * or a different colour saying "this is a different kind of jump" when it is the same one.
 *
 * ## It is `.agavi-pulse`, and it was not
 *
 * This used to hand-roll an inline `outline`, held flat for 1800ms and then dropped. Two things were
 * wrong with that and both were visible. It had no ENVELOPE — the app's pointing gesture is a sharp
 * attack and a slow decay (~55ms in, ~190ms held, ~455ms out), and a ring that snaps on, sits still
 * for nearly two seconds and vanishes reads as a state the card has entered rather than as a finger
 * pointing at it. And it was an `outline` with an offset rather than a `box-shadow`, so anywhere an
 * ancestor scrolls or hides overflow it came out clipped.
 *
 * So it now uses the same class as every other "here, this is the one" in the app: copy, paste, undo,
 * jump-from-the-map, and the board at all four depths. `box-shadow` follows the element's own
 * border-radius, which is why it sits correctly on a rounded block card.
 *
 * The remove-reflow-add is not superstition: re-adding a class the element already carries does not
 * restart a CSS animation, so pressing Fix twice for the same block would show the ring once.
 * `flashBlock` on the workspace page solves the same problem by clearing state across a frame.
 */
export function revealElement(el: Element) {
  bringIntoView(el);
  const node = ringTarget(el);
  node.classList.remove(PULSE_CLASS);
  void node.offsetWidth;
  node.classList.add(PULSE_CLASS);
  // Taken off again rather than left to the animation's own end: with motion reduced the class holds
  // a static ring instead of animating, so nothing else would ever clear it.
  window.setTimeout(() => node.classList.remove(PULSE_CLASS), PULSE_MS);
}

/**
 * The element being spoken right now, ringed until the next one takes over.
 *
 * `revealElement` above self-clears after 1800ms, which is right for a glance and wrong here: an
 * utterance can run longer or shorter than that, so the ring would either vanish mid-sentence or
 * outlive it. One at a time, module-level, because only one thing can be spoken at once.
 */
let highlighted: HTMLElement | null = null;
function highlight(el: Element | null) {
  if (highlighted) {
    highlighted.style.outline = '';
    highlighted.style.outlineOffset = '';
    highlighted = null;
  }
  if (!el) return;
  const node = ringTarget(el);
  bringIntoView(node);
  node.style.outline = '3px solid var(--color-ws-primary)';
  node.style.outlineOffset = '2px';
  highlighted = node;
}

/**
 * Speaking the read-through.
 *
 * The bug this replaces: every utterance was queued at once, and `onstart` called back into React
 * to move the highlight. That re-render rebuilt the rows array, an effect keyed on its identity
 * fired, and it called `cancel()` — so playback stopped itself after the first item every time.
 * The lesson is narrow and worth keeping: never key a `cancel()` on a value that playback itself
 * causes to change. `stopKey` here is the scan revision, which playback cannot touch.
 *
 * Utterances are still queued together rather than chained through `onend`, because a chain drops
 * the rest of the queue if any one utterance errors, and `speechSynthesis` errors readily — a
 * missing voice, a backgrounded tab.
 */
/**
 * A row, plus what to SAY for it where that differs from what is shown.
 *
 * Images, sound clips, formulas and reactions carry their kind on screen as a chip beside the row,
 * so the words are already there for a reader of the panel. A listener pressing Play has no chip,
 * and that is the listener the panel exists to stand in for — so the kind goes into the utterance
 * and stays out of the label, rather than appearing twice in one row.
 *
 * Controls are the other way round and stay as they were: they have no chip, so their role and
 * state go into `name` and are both shown and spoken.
 */
type ReadingRow = OutlineRow & { speech?: string };

function useReadAloud(rows: ReadingRow[], stopKey: string, onIndex: (index: number) => void) {
  const canSpeak = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [speakingIndex, setSpeakingIndex] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);

  const stop = useCallback(() => {
    if (canSpeak) window.speechSynthesis.cancel();
    setSpeakingIndex(null);
    setPaused(false);
    highlight(null);
  }, [canSpeak]);

  /**
   * Pause where it is, resume where it left off.
   *
   * `cancel()` would be the easy thing and the wrong one: it drops the queue, so carrying on would
   * mean starting the whole task again from the top. `pause`/`resume` hold the position, including
   * mid-sentence, which is the point of having a pause at all.
   */
  const pause = useCallback(() => {
    if (!canSpeak) return;
    window.speechSynthesis.pause();
    setPaused(true);
  }, [canSpeak]);

  const resume = useCallback(() => {
    if (!canSpeak) return;
    window.speechSynthesis.resume();
    setPaused(false);
  }, [canSpeak]);

  // A voice that carries on reading a task the teacher has left is the worst version of this.
  useEffect(() => stop, [stop]);
  useEffect(() => { stop(); }, [stopKey, stop]);

  const play = useCallback((indices: number[]) => {
    if (!canSpeak) return;
    window.speechSynthesis.cancel();
    setPaused(false);
    const last = indices[indices.length - 1];
    for (const i of indices) {
      const row = rows[i];
      if (!row) continue;
      const u = new SpeechSynthesisUtterance(row.speech ?? row.name);
      u.onstart = () => { setSpeakingIndex(i); onIndex(i); highlight(row.el); };
      if (i === last) {
        u.onend = () => { setSpeakingIndex(null); highlight(null); };
      }
      u.onerror = () => { setSpeakingIndex(null); highlight(null); };
      window.speechSynthesis.speak(u);
    }
  }, [canSpeak, rows, onIndex]);

  /**
   * Read on from here — the whole rest of the task, not just this one row.
   *
   * Lives here rather than at the two call sites because both were building the index list by hand
   * and only one of them had it: the skip buttons carried playback forward, and clicking a card
   * did not, so a listener who heard something interesting and clicked it went on hearing whatever
   * was already queued.
   */
  const playFrom = useCallback((index: number) => {
    play(rows.map((_, k) => k).slice(index));
  }, [play, rows]);

  return { canSpeak, speakingIndex, paused, play, playFrom, stop, pause, resume };
}

/**
 * Role and state words, as LITERAL `t()` calls.
 *
 * Built from the value they describe, these were template keys and tripped the dynamic-key ceiling
 * in `invariants.test.ts` — the fourth time in this area. A `Record` lookup would be worse:
 * invisible to that ceiling AND to the every-key-exists check. Exhaustive switches mean a new role
 * or state fails to compile until it has a key in both locales.
 */
function roleWord(role: ControlRole, t: (key: string) => string): string {
  switch (role) {
    case 'button': return t('task.bottomBar.a11yRole_button');
    case 'link': return t('task.bottomBar.a11yRole_link');
    case 'checkbox': return t('task.bottomBar.a11yRole_checkbox');
    case 'radio': return t('task.bottomBar.a11yRole_radio');
    case 'combobox': return t('task.bottomBar.a11yRole_combobox');
    case 'textbox': return t('task.bottomBar.a11yRole_textbox');
    case 'slider': return t('task.bottomBar.a11yRole_slider');
    case 'switch': return t('task.bottomBar.a11yRole_switch');
    case 'tab': return t('task.bottomBar.a11yRole_tab');
    case 'menuitem': return t('task.bottomBar.a11yRole_menuitem');
    case 'option': return t('task.bottomBar.a11yRole_option');
    case 'control': return t('task.bottomBar.a11yRole_control');
  }
}

function stateWord(state: ControlState, t: (key: string) => string): string {
  switch (state) {
    case 'checked': return t('task.bottomBar.a11yState_checked');
    case 'notChecked': return t('task.bottomBar.a11yState_notChecked');
    case 'mixed': return t('task.bottomBar.a11yState_mixed');
    case 'expanded': return t('task.bottomBar.a11yState_expanded');
    case 'collapsed': return t('task.bottomBar.a11yState_collapsed');
    case 'disabled': return t('task.bottomBar.a11yState_disabled');
    case 'empty': return t('task.bottomBar.a11yState_empty');
  }
}

/**
 * A control as a reader would announce it: NAME, then role, then state, then value.
 *
 * Name first because every real reader does it — NVDA "Numerical answer, edit, blank", JAWS
 * "Numerical answer edit", VoiceOver "Numerical answer, edit text". This deliberately led with the
 * role for a while, on the theory that hearing "checkbox, not checked" repeat down a list made the
 * pattern audible. It did, and it also taught its first reader something false: that a listener
 * learns "text box" before learning which box, and so has to be told the field wants a number by
 * some means other than its name. That is backwards, and the inversion is what made it look true.
 * Leading with the name keeps the repetition audible anyway — the role still repeats, one slot
 * later — while putting the distinguishing word where a reader actually puts it.
 *
 * Every part is read off the DOM — an explicit `role`, `aria-checked`, `aria-valuetext` — rather
 * than invented. That boundary is the whole reason this is safe to show: it reports what is exposed
 * to assistive technology, and does not claim to be any particular reader's wording. Real ones
 * still vary in verbosity and in the words themselves.
 *
 * A nameless control keeps its row and says so. It is a defect, ours not the teacher's, and the
 * people most likely to be reading this mode are the ones who would report it.
 */
function controlSpeech(row: OutlineRow, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const parts: string[] = [];
  parts.push(row.name || t('task.bottomBar.a11yReadNoName'));
  if (row.role) parts.push(roleWord(row.role, t));
  for (const state of row.states ?? []) parts.push(stateWord(state, t));
  if (row.value) parts.push(row.value);
  return parts.join(', ');
}

/**
 * The kinds a reader NAMES before it reads them, and the word it uses.
 *
 * A real screen reader does not hand you an image's alt text bare — NVDA says "graphic", JAWS
 * "graphic", VoiceOver "image" — and without that a listener cannot tell a described picture from a
 * sentence. The emulator was handing over the alt text alone, so it under-reported what a student
 * hears and made a picture indistinguishable from prose in the read-through.
 *
 * Formulas and reactions get the same treatment for the same reason: spoken, `2 H2 + O2` is a run
 * of letters and numbers with nothing to mark it as chemistry, and a listener needs to know a
 * notation has begun before trying to parse one. A sound clip's row has no name at all, so without
 * the word it is silence.
 *
 * Headings are excluded deliberately, though a reader does announce them: a heading's row reads as
 * the words it contains, the level is not something a teacher sets here, and the visual label beside
 * the row already says so. Text and controls are handled elsewhere — see `controlSpeech`.
 */
/**
 * The emulator NEVER synthesises a kind word. These two are not synthesis.
 *
 * The rule (owner): the chip is the teacher's index and is never spoken; the spoken text is what a
 * reader is actually given. So a word like "Image" or "Animation" belongs in the MARKUP, where a
 * real reader will say it, and the emulator then reproduces it for free because it is genuinely
 * there. It is put in the markup exactly when the element's own role fails to convey the medium —
 * an MCQ option announces as "radio button", so its label carries "Image:" or "Animation:"; a
 * loose `<img>` announces as "graphic", so its alt stays bare.
 *
 * `image` used to be in this set, on the reasoning that a reader DOES say "graphic" so echoing it
 * was accurate. That is true and still the wrong mechanism: it made the panel the author of a word
 * rather than the transcriber of one, and it is how "Formula:" survived here for so long. An image
 * that needs the word now gets it from its own label.
 *
 * `table` is a row whose NAME is its summary — a reader really does state a table's dimensions on
 * entering it, and `tableSummary` builds that into the name rather than prefixing it.
 *
 * `feedback` is the one genuine exception, and it is flagged as a markup gap rather than defended:
 * the verdict lives in an `aria-hidden` glyph and a CSS tint, so a listener is given NOTHING. The
 * preface stands in for information the markup fails to carry. Closing that in the markup would
 * let this set empty out entirely.
 */
const SPOKEN_KINDS = new Set<OutlineRow['kind']>(['table', 'feedback']);

/**
 * What a reader states on entering a table: its name and its size.
 *
 * Both shown and spoken, unlike the kind prefix — the size is the row's content here, not dressing
 * around it. A table with no caption is named by its dimensions alone rather than by nothing.
 */
function tableSummary(row: OutlineRow, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const size = t('task.bottomBar.a11yReadTableSize', { rows: row.table?.rows ?? 0, columns: row.table?.columns ?? 0 });
  return row.name ? `${row.name}, ${size}` : size;
}

/**
 * The kind spoken ahead of the row's own words, for the kinds a reader names.
 *
 * FEEDBACK names its symbol where the chip only draws one: the eye takes the verdict from the icon
 * in the same glance, and a listener has no icon. It does NOT go on to state the verdict in words —
 * that is the symbol's job, and saying both was the same thing twice.
 */
function spokenWithKind(row: OutlineRow, t: (key: string, opts?: Record<string, unknown>) => string): string | undefined {
  if (!SPOKEN_KINDS.has(row.kind) || !row.name) return undefined;
  const kind = row.kind === 'feedback' && row.evaluation
    ? spokenFeedbackPreface(row.evaluation, t)
    : kindLabel(row, t);
  return kind ? `${kind}: ${row.name}` : undefined;
}

/** The glyph beside a kind's label. Text and controls have none, and need none. */
function KindIcon({ row }: { row: OutlineRow }) {
  const cls = 'h-3 w-3';
  // The same filled Play the library's own animated badge uses, so one glyph means "this moves"
  // wherever a teacher meets it.
  if (row.animated) return <Play aria-hidden='true' className={cls} fill='currentColor' />;
  if (row.kind === 'image' || row.mediaKind === 'image') return <ImageIcon aria-hidden='true' className={cls} />;
  if (row.mediaKind === 'audio') return <Volume2 aria-hidden='true' className={cls} />;
  if (row.kind === 'formula') return <Sigma aria-hidden='true' className={cls} />;
  if (row.kind === 'reaction') return <FlaskConical aria-hidden='true' className={cls} />;
  // The verdict, in the same tick/cross/dash the chips use everywhere else — and the reason the
  // feedback chip's WORD can stay at one. Takes the whole row rather than the kind because this is
  // the first icon that depends on more than which kind it is.
  if (row.kind === 'feedback' && row.evaluation) return <ChipEvalIcon evaluation={row.evaluation} />;
  return null;
}

/**
 * The kind label, where it says something.
 *
 * `text` and `control` return null: nearly every row is text, so the word appeared on all of them
 * and distinguished nothing — a label that is always the same is noise with a heading's weight.
 * The rest stay, because those rows genuinely read differently: a heading, an image, a sound, and
 * a block that is nothing but a formula or a reaction. Controls no longer reach the read-through at
 * all, so losing their label costs nothing.
 *
 * Still LITERAL `t()` calls, never a template key or a `Record`: a template key trips the
 * dynamic-key ceiling in `invariants.test.ts`, and a Record would be invisible to that ceiling AND
 * to the every-key-exists check, which is the worst of the two. The exhaustive switch means a new
 * kind fails to compile rather than rendering an identifier.
 */
function kindLabel(row: OutlineRow, t: (key: string) => string): string | null {
  /*
   * ANIMATION outranks the kind, on an image or on a control.
   *
   * "Image" is wrong about a picture that moves, and on an MCQ media option the kind is `control`,
   * which carries no label at all — so an animated option read as two unexplained sentences. The
   * word is doing what Formula and Reaction do: telling a teacher why this row reads differently
   * from the ones around it.
   */
  if (row.animated) return t('task.bottomBar.a11yReadKind_animation');
  /* A control that WRAPS media is labelled by what it wraps. An MCQ or Ordering media option is a
     control whose name sits on the tile, so the kind alone ("control", which is unlabelled) left
     an image option with no chip at all — the one row kind a teacher most wants to pick out. */
  if (row.mediaKind === 'image') return t('task.bottomBar.a11yReadKind_image');
  if (row.mediaKind === 'audio') return t('task.bottomBar.a11yReadKind_media');
  switch (row.kind) {
    case 'heading': return t('task.bottomBar.a11yReadKind_heading');
    case 'image': return t('task.bottomBar.a11yReadKind_image');
    case 'media': return t('task.bottomBar.a11yReadKind_media');
    case 'formula': return t('task.bottomBar.a11yReadKind_formula');
    case 'reaction': return t('task.bottomBar.a11yReadKind_reaction');
    case 'table': return t('task.bottomBar.a11yReadKind_table');
    // One word, because the icon beside it carries the verdict. The spoken form says the rest.
    case 'feedback': return t('task.bottomBar.a11yReadKind_feedback');
    case 'control':
    case 'text': return null;
  }
}

/**
 * The read-through: the teacher's own content, in the order a listener receives it.
 *
 * A STEP-THROUGH rather than a list, because sequence is the thing being inspected and a list of
 * thirty names shows you all of it at once and none of it in place. Next and Previous walk the
 * content, each step highlighting the real element and showing what would be announced — so the
 * check is "does this make sense read aloud", answered one piece at a time, which is how a listener
 * actually receives it. It also replaces a "Show me" press per row, and needs no side-by-side
 * layout, which is what makes it work on a phone.
 *
 * Play speaks the sequence with the browser's own voice. Deliberately labelled as Agavi reading the
 * content and never as a screen-reader test: JavaScript cannot read the platform accessibility
 * tree, so this is our reconstruction, and a teacher who believes otherwise would trust it for
 * something it cannot do.
 */
/**
 * The note and the transport, ANCHORED — they sit outside the scrolling list.
 *
 * Both were inside it, which meant scrolling to row twenty scrolled the only Stop button off the
 * screen. Playback is a state you are in while reading the list, so its controls have to stay
 * reachable the whole time; the note has to be read before the list, which is the other reason it
 * belongs above rather than in.
 */
function ReadThroughControls({ rows, speech, activeIndex, full, onFull, onStep, compact }: {
  rows: OutlineRow[];
  speech: ReturnType<typeof useReadAloud>;
  activeIndex: number | null;
  full: boolean;
  onFull: (next: boolean) => void;
  onStep: (index: number) => void;
  /** A narrow, short panel — the phone-landscape rail. Drops what it cannot afford. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const { canSpeak, speakingIndex, paused, playFrom, stop, pause, resume } = speech;
  const playing = speakingIndex !== null;
  /**
   * Where Play starts.
   *
   * From the selected row when there is one, so "read on from here" is one press rather than
   * listening through everything before it again. From the top otherwise.
   */
  const from = activeIndex ?? 0;
  const fromHere = activeIndex !== null && activeIndex > 0;
  /**
   * What the one round button will do next. Its ACCESSIBLE NAME only — there is no caption.
   *
   * It was shown as text too, so the transport carried a line reading "Play from 7". That is a
   * sentence about a decision the teacher has just made, in a panel where the answer is already on
   * screen: the row is selected, and it has its own play button if that one row is what they want.
   * Play means "from here" and each row means "this one"; neither needs saying.
   *
   * A listener keeps it, because they have neither the selection nor the row's button under their
   * finger — which is exactly what an `aria-label` is for.
   */
  const actionLabel = playing
    ? (paused ? t('task.bottomBar.a11yReadResume') : t('task.bottomBar.a11yReadPause'))
    : (fromHere ? t('task.bottomBar.a11yReadPlayFrom', { at: from + 1 }) : t('task.bottomBar.a11yReadPlayAll'));
  /**
   * Wraps, so the end is not a dead stop when you are hunting for something — and CARRIES playback
   * with it: skipping mid-listen means "not this one, the next one", so stopping to make you press
   * play again is the opposite of what the button is for.
   *
   * Paused is the exception. There the queue is being held deliberately, and jumping elsewhere
   * abandons that position, so the transport re-arms as "Play from N" rather than quietly
   * un-pausing somewhere new.
   */
  const step = (delta: number) => {
    if (!rows.length) return;
    const next = ((activeIndex ?? 0) + delta + rows.length) % rows.length;
    const carryOn = playing && !paused;
    if (!carryOn) stop();
    onStep(next);
    if (carryOn) playFrom(next);
  };

  return (
    <div className='shrink-0 space-y-2'>
      {/* FIRST, before any of it. Set as a NOTE — italic, muted, no frame — which is how this app
          states guidance everywhere (see TOLERANCE_HINT_CLASS, and the advisories on the rows). A
          card gave it the weight of chrome, competing with the content it introduces.

          Dropped on the landscape rail. It is three lines of standing caveat in a panel about 390px
          tall, and it is the same three lines every time — so it pushed the findings, which change,
          below the fold in favour of something that does not. A teacher meets it on every other
          surface the review has. */}
      {!compact && (
        <p className='pb-1 text-[11px] italic leading-snug text-ws-muted'>
          {t('task.bottomBar.a11yReadDisclaimer')}
        </p>
      )}

      {/*
        ONE ROW: transport left, scope right, on every surface.

        The switch used to have a full-width row of its own, above the note and deliberately far
        from the transport so it would not be pressed by mistake. Putting the two at opposite ends
        of one line keeps them apart while giving the row back — which a phone needs and a docked
        panel does not mind, and which is worth more than the separation was.

        Stop is gone with it. Play/Pause already covers "make it stop", and Previous, Next and
        picking a row all re-arm from wherever you land, so the only thing it did that nothing else
        does is clear the queue — while sitting disabled most of the time to offer it.
      */}
      <div className='flex items-center gap-2'>
        {/* The TRANSPORT is conditional; the switch is not. "Your Content" on a task with no
            authored text is an empty list, and hiding the scope switch there left no way to widen
            it — the one moment a teacher most needs the other scope is the moment this used to take
            it away. */}
        {rows.length > 0 && canSpeak && (
          <>
          {/* The sound block's transport, in the same shape: one round primary button that toggles,
              with the Play triangle nudged a pixel right because a triangle centred by its bounding
              box looks off-centre. */}
          {/* Step one at a time, and PLAY from wherever you land. Skipping is how you find the part
              you want without listening through what comes before it. */}
          <div className='flex shrink-0 items-center gap-1.5'>
            <button
              type='button'
              onClick={() => step(-1)}
              aria-label={t('task.bottomBar.a11yReadPrev')}
              className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-ws-border text-ws-text hover:bg-ws-hover'
            >
              <SkipBack aria-hidden='true' className='h-3.5 w-3.5' />
            </button>
            <button
              type='button'
              onClick={() => {
                if (!playing) playFrom(from);
                else if (paused) resume();
                else pause();
              }}
              aria-label={actionLabel}
              className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ws-primary text-ws-btn-on-primary hover:opacity-90'
            >
              {playing && !paused
                ? <Pause aria-hidden='true' className='h-4 w-4' />
                : <Play aria-hidden='true' className='h-4 w-4 translate-x-[1px]' />}
            </button>
            <button
              type='button'
              onClick={() => step(1)}
              aria-label={t('task.bottomBar.a11yReadNext')}
              className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-ws-border text-ws-text hover:bg-ws-hover'
            >
              <SkipForward aria-hidden='true' className='h-3.5 w-3.5' />
            </button>
          </div>
          </>
        )}

          {/* One switch, off by default. The teacher's own writing is the common case by a distance;
              the whole task is the occasional one — checking it works heard, or handing it to a
              tester who can tell us we built the controls wrong.

              One button carrying both labels and the track, as `sensor-simulate-bar` does — that
              gives a 44px tap target instead of a 20px one, and names both positions rather than
              leaving the words beside it as stray text with no stated relationship to the control. */}
          <Switch
            on={full}
            onChange={onFull}
            label={`${t('task.bottomBar.a11yScopeAuthored')} / ${t('task.bottomBar.a11yScopeFull')}`}
            /* SHORT on the chips, long in the label. The visible pair only has to distinguish two
               positions on a control a teacher is looking straight at; the accessible name has to
               stand on its own, because a listener meets it with no switch in front of them. The
               `states` are `aria-hidden` for exactly this reason, so the two can differ. */
            states={{ off: t('task.bottomBar.a11yScopeAuthoredShort'), on: t('task.bottomBar.a11yScopeFullShort') }}
            className='min-w-0 flex-1'
          />
      </div>

    </div>
  );
}

function ReadThroughList({ rows, speech, activeIndex, onPick, onReveal, rowRefs }: {
  rows: OutlineRow[];
  speech: ReturnType<typeof useReadAloud>;
  activeIndex: number | null;
  onPick: (index: number) => void;
  onReveal: (el: Element) => void;
  /** So a badge press on the task can bring its row into view here. */
  rowRefs: React.MutableRefObject<(HTMLLIElement | null)[]>;
}) {
  const { t } = useTranslation();
  const { canSpeak, speakingIndex, play, stop } = speech;

  if (rows.length === 0) {
    return <p className='py-6 text-center text-sm text-ws-muted'>{t('task.bottomBar.a11yReadNothing')}</p>;
  }

  return (
    /**
     * Everything at once, numbered, each with its own play.
     *
     * Play-all reads the task through; a single row answers "what does THIS sound like", which is
     * the question you have when one row looks wrong. The number is also the handle the overlay
     * shares — badge 7 on the task is row 7 here.
     */
    <ol className='space-y-1'>
      {rows.map((r, i) => {
        const label = kindLabel(r, t);
        return (
          <li key={i} ref={(el) => { rowRefs.current[i] = el; }}>
            <div className={`flex items-start gap-2 rounded-md border px-2 py-1.5 transition-colors ${
              speakingIndex === i ? 'border-ws-primary bg-ws-primary/15'
                : activeIndex === i ? 'border-ws-primary bg-ws-primary/10'
                : 'border-ws-border bg-ws-btn'}`}>
              <button
                type='button'
                onClick={() => { onPick(i); onReveal(r.el); }}
                aria-current={activeIndex === i ? 'true' : undefined}
                className='flex min-w-0 flex-1 items-start gap-2 text-left'
              >
                <span className='mt-px shrink-0 rounded bg-ws-hover px-1.5 text-[10px] font-semibold tabular-nums text-ws-muted'>
                  {i + 1}
                </span>
                <span className='min-w-0 flex-1'>
                  {label && (
                    <span className='mb-0.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-ws-muted'>
                      <KindIcon row={r} />
                      {label}
                    </span>
                  )}
                  <span className={`block text-xs text-ws-text ${r.kind === 'heading' ? 'font-semibold' : ''}`}>
                    {r.name || <span className='italic text-ws-warning-fg'>{t('task.bottomBar.a11yReadNoAlt')}</span>}
                  </span>

                </span>
              </button>
              {canSpeak && r.name && (
                <button
                  type='button'
                  onClick={() => (speakingIndex === i ? stop() : play([i]))}
                  aria-label={speakingIndex === i
                    ? t('task.bottomBar.a11yReadStop')
                    : t('task.bottomBar.a11yReadPlayOne', { at: i + 1 })}
                  className='mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded text-ws-primary hover:bg-ws-hover dark:text-ws-text-link'
                >
                  {speakingIndex === i
                    ? <Square aria-hidden='true' className='h-3 w-3 fill-current' />
                    : <Play aria-hidden='true' className='h-3 w-3 translate-x-[1px]' />}
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Numbered badges over the task itself, sharing their numbers with the list.
 *
 * The list answers "what does my content sound like"; this answers "which piece is that". Badge 7
 * on the task is row 7 in the panel, either one selects the other, so nothing has to be hunted by
 * reading a name and scanning for it. It also replaces a "Show me" press per row.
 *
 * A fixed-position layer over the real elements rather than markers injected into them: injecting
 * would change the layout being inspected, and this has to sit on top of a task it must not
 * disturb. Positions therefore come from `getBoundingClientRect` and are recomputed on scroll and
 * resize — `true` on the scroll listener because the task scrolls in an inner container, not the
 * window, and a bubbling listener never hears that.
 */
/**
 * Where the task is, and where each row's element is inside it.
 *
 * Shared because the badges and the phone frame's bars all hang off the same box: measured, not
 * assumed, since the task scrolls inside its own container. The scroll listener is capturing for
 * that reason — a bubbling one never hears an inner scroller.
 */
/**
 * The attributes a scan's answer can actually turn on.
 *
 * An `attributeFilter` rather than all attributes, and the reason is a loop: `revealElement` and the
 * read-aloud highlight both write inline `style` onto elements INSIDE the observed tree, so an
 * unfiltered observer would see its own ring, re-scan, re-render the panel, and go round again.
 * Naming the attributes cuts that out without having to special-case the ring.
 *
 * Taken from what `preview-a11y-scan` reads, so the two drift together or not at all.
 */
const SCANNED_ATTRIBUTES = [
  'alt', 'aria-checked', 'aria-disabled', 'aria-expanded', 'aria-hidden', 'aria-label',
  'aria-labelledby', 'aria-valuenow', 'aria-valuetext', 'data-media-transcript',
  'data-reaction-formula', 'disabled', 'role', 'type', 'class',
];

/** Long enough that a burst of typing is one scan, short enough to feel live. */
const LIVE_SCAN_DEBOUNCE_MS = 250;

/**
 * Keep the read-through honest while a teacher is USING the task.
 *
 * The panel used to scan on four page-level events and nothing else, on the stated grounds that
 * "nothing in Preview drifts in real time". That was wrong in the way that matters most: answering a
 * question is the single most common thing a teacher does in Preview, and every one of them moves
 * what a screen reader would say. Type into a numerical field and its row still reads "empty". Pick
 * an option and the outline still calls it not checked. The panel was a photograph of the moment it
 * opened, which is the worst kind of wrong for a tool whose whole claim is "this is what a student
 * hears".
 *
 * ## Why one signal is not enough
 *
 * A `MutationObserver` alone does not see it. `screenReaderOutline` reads `el.value` and
 * `el.checked`, which are DOM PROPERTIES — typing changes the property and leaves the attribute
 * exactly where it was, so the observer is never called at all. So this listens for two different
 * things and funnels them into one debounced scan:
 *
 *   - `input` / `change`, captured, for values the observer cannot see.
 *   - the observer, for everything structural: feedback boxes arriving, a state toggling, a block
 *     swapping what it renders.
 *
 * ## What it deliberately does not do
 *
 * It does not scan while the task is being READ ALOUD. The original objection to observing —
 * "a findings list that reshuffles while being read is worse than one that is a moment stale" — is
 * still right, and playback holds an index into the row list; re-scanning under it would move the
 * ground mid-sentence. One catch-up scan runs when playback stops.
 */
function useLiveScan(getScanRoot: () => Element | null, scan: () => void, speaking: boolean) {
  const scanRef = useRef(scan);
  scanRef.current = scan;

  useEffect(() => {
    const root = getScanRoot();
    if (!root) return;
    // The scan that was skipped while a voice was talking, run now that it has stopped.
    if (speaking) return;
    scanRef.current();

    let timer = 0;
    const bump = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => scanRef.current(), LIVE_SCAN_DEBOUNCE_MS);
    };

    const observer = new MutationObserver((records) => {
      // The badge layer is the panel's OWN output, rendered into the task through a portal. Reacting
      // to it would be the panel scanning because the panel drew something.
      if (records.every(r => (r.target as Element).closest?.('[data-a11y-badge-layer]'))) return;
      bump();
    });
    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: SCANNED_ATTRIBUTES,
    });
    // Capture, because a block may well stop these from bubbling on their way up.
    root.addEventListener('input', bump, true);
    root.addEventListener('change', bump, true);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
      root.removeEventListener('input', bump, true);
      root.removeEventListener('change', bump, true);
    };
  }, [getScanRoot, speaking]);
}


/**
 * Where each badge sits, in the task's own content coordinates.
 *
 * The distinction from `useTaskGeometry` above is the whole fix. Those bars are `position: fixed`,
 * so they genuinely have to track the viewport and re-measure as the page scrolls. Badges do not:
 * they live INSIDE the scrolled content, so an offset from the content's own origin is constant
 * while scrolling and CSS moves them for free.
 *
 * Which is why there is no scroll listener here. The old version recomputed viewport rects in a
 * capturing scroll handler and pushed them through React state, so badges painted a frame behind
 * content the browser had already moved — visibly lagging, clamped into a pile at the top edge on
 * the way out, and frozen during an overscroll bounce, which no listener can see.
 *
 * A `ResizeObserver` covers what actually invalidates an offset: the content growing or reflowing,
 * a block expanding, feedback appearing. That last one was silently broken before — nothing
 * re-measured on a re-render that changed layout without a scroll or a resize.
 */
/**
 * What a badge should actually point AT.
 *
 * Reported: a badge numbered in the forties sat over nothing a teacher could identify. `sr-only`
 * content is scanned on purpose — the slider's scale line and marker summary reach a listener only
 * that way — but Tailwind's `sr-only` is a 1x1 absolutely-positioned box, so a badge anchored to it
 * lands on a pinpoint attached to nothing visible, and several of them land on top of each other.
 *
 * So an element with no real box hands the badge to the nearest ancestor that has one: the block
 * the hidden text belongs to. The number then sits on the thing it is describing, which is the only
 * way "what is 44" has an answer.
 */
const MIN_ANCHOR_PX = 4;

function anchorFor(el: Element): Element {
  let cur: Element | null = el;
  while (cur) {
    const b = cur.getBoundingClientRect();
    if (b.width >= MIN_ANCHOR_PX && b.height >= MIN_ANCHOR_PX) return cur;
    cur = cur.parentElement;
  }
  return el;
}

/**
 * Nudge badges apart when they would land on each other.
 *
 * Reported: 30 and 31 overlapped. Corner-anchoring guarantees this eventually — two blocks flush in
 * a column share an x, an inline control sits at its paragraph's corner, and anything handed to an
 * ancestor by `anchorFor` lands exactly where that ancestor's own badge is.
 *
 * Shifted along x rather than y, and in reading order, so a run reads left to right like the list
 * it represents. Comparing against everything already placed rather than just the previous one:
 * three badges at one corner have to end up in three different places, not two.
 */
function deCollide(placed: Offset[]): Offset[] {
  const out: Offset[] = [];
  for (const p of placed) {
    // A row with no place on the map keeps its slot, so a badge's number still matches its row.
    if (!p) { out.push(null); continue; }
    const spot = { ...p };
    // Bounded, so a pathological stack cannot walk a badge off the far side of the task.
    for (let guard = 0; guard < 24; guard += 1) {
      const clash = out.some(q => q != null && Math.abs(q.top - spot.top) < BADGE_PX && Math.abs(q.left - spot.left) < BADGE_PX);
      if (!clash) break;
      spot.left += BADGE_PX + 2;
    }
    out.push(spot);
  }
  return out;
}

function useBadgeOffsets(rows: OutlineRow[], getRoot: () => Element | null) {
  const [layer, setLayer] = useState<Element | null>(null);
  const [offsets, setOffsets] = useState<Offset[]>([]);

  useLayoutEffect(() => {
    /**
     * The layer that CONTAINS the rows, not always the task's.
     *
     * There is more than one surface now. While Preview's submission summary is up the task is
     * `aria-hidden` and the dialog is what gets read, so the numbers have to land on the dialog —
     * a read-through whose list is numbered 1..6 against a task showing no numbers at all is a map
     * of somewhere else. Each surface carries its own layer and the rows pick theirs.
     *
     * Matched on the layer's PARENT rather than the layer itself, because a layer is a positioned
     * sibling of the content it pins, never an ancestor of it — the same relationship the
     * per-row `contains` check below already relies on.
     */
    const layers = [...document.querySelectorAll('[data-a11y-badge-layer]')];
    const node = (rows[0] && layers.find(l => l.parentElement?.contains(rows[0]!.el)))
      ?? getRoot()?.querySelector('[data-a11y-badge-layer]') ?? null;
    setLayer(node);
    if (!node) return;
    const measure = () => {
      const origin = node.getBoundingClientRect();
      const placed = rows.map(r => {
        // A row from OUTSIDE the task — the results bar, Submit — has no place on a map of the
        // task. Its offset would land below the layer and be clipped away, so it gets no badge at
        // all and keeps its number in the list, where it still reads in order.
        if (!node.parentElement?.contains(r.el)) return null;
        const b = anchorFor(r.el).getBoundingClientRect();
        return { top: b.top - origin.top, left: b.left - origin.left };
      });
      setOffsets(deCollide(placed));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [rows, getRoot]);

  return { layer, offsets };
}


/**
 * The read-through on a phone: a frame around the task, not a sheet over it.
 *
 * A full-screen list was the wrong shape here. The whole question is "what does THAT part sound
 * like", and answering it meant closing the list, finding the part, and opening the list again. So
 * the task stays on screen with its badges, the transport sits at the top of it, and the one piece
 * currently selected shows its transcript at the bottom. Tapping a number selects it — it does not
 * start playing, because choosing what to hear and choosing to hear it are separate decisions —
 * and the transport then reads "Play from 7".
 *
 * Landscape puts both in a rail on the right instead, since height is the scarce axis there and a
 * bar top and bottom would leave the task a letterbox.
 */
function PhoneReadFrame({ rows, getRoot, speech, activeIndex, onPick, full, onFull }: {
  rows: OutlineRow[];
  getRoot: () => Element | null;
  speech: ReturnType<typeof useReadAloud>;
  activeIndex: number | null;
  onPick: (index: number) => void;
  full: boolean;
  onFull: (next: boolean) => void;
}) {
  const { t } = useTranslation();
  const { canSpeak, speakingIndex, paused, play, playFrom, stop, pause, resume } = speech;
  const playing = speakingIndex !== null;
  const shown = speakingIndex ?? activeIndex;
  const current = shown !== null ? rows[shown] : null;
  const from = activeIndex ?? 0;
  const fromHere = activeIndex !== null && activeIndex > 0;
  const label = playing
    ? (paused ? t('task.bottomBar.a11yReadResume') : t('task.bottomBar.a11yReadPause'))
    : (fromHere
      ? t('task.bottomBar.a11yReadPlayFrom', { at: from + 1 })
      : t('task.bottomBar.a11yReadPlayAll'));

  /**
   * Step one row at a time, CARRYING playback — the desktop transport's rule, and the phone had no
   * way to do it at all. Skipping is how you find the part you want without listening through
   * everything before it, and a read-through is the one place that matters most.
   */
  const step = (delta: number) => {
    if (!rows.length) return;
    const next = ((activeIndex ?? 0) + delta + rows.length) % rows.length;
    const carryOn = playing && !paused;
    if (!carryOn) stop();
    onPick(next);
    if (carryOn) playFrom(next);
  };

  /**
   * `flex-wrap`, because the same row serves two very different widths: a full-width bar in
   * portrait, and a 224px rail in landscape. Adding Previous and Next took the landscape row to five
   * controls and pushed Close off the end — `ml-auto` kept right-aligning it past the edge, so it
   * looked placed rather than lost. Wrapped, Close drops to its own line and stays right.
   */
  /**
   * Three controls, not four, and they share their line with the scope switch.
   *
   * Stop is the one left out. Play/Pause already covers "make it stop", Previous, Next and tapping a
   * badge all re-arm from a chosen row, so the only thing Stop did that nothing else does is clear
   * the queue — and it was rendered disabled most of the time to do it. A labelled switch plus four
   * 36px buttons does not fit a 390px line; with three it does, and the strip loses a whole row.
   */
  const transport = (
    <div className='flex shrink-0 items-center gap-1.5'>
      {canSpeak && (
        <>
          <button
            type='button'
            onClick={() => step(-1)}
            aria-label={t('task.bottomBar.a11yReadPrev')}
            className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-ws-border text-ws-text hover:bg-ws-hover'
          >
            <SkipBack aria-hidden='true' className='h-3.5 w-3.5' />
          </button>
          <button
            type='button'
            onClick={() => {
              if (!playing) playFrom(from);
              else if (paused) resume();
              else pause();
            }}
            aria-label={label}
            className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ws-primary text-ws-btn-on-primary hover:opacity-90'
          >
            {playing && !paused
              ? <Pause aria-hidden='true' className='h-4 w-4' />
              : <Play aria-hidden='true' className='h-4 w-4 translate-x-[1px]' />}
          </button>
          <button
            type='button'
            onClick={() => step(1)}
            aria-label={t('task.bottomBar.a11yReadNext')}
            className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-ws-border text-ws-text hover:bg-ws-hover'
          >
            <SkipForward aria-hidden='true' className='h-3.5 w-3.5' />
          </button>
        </>
      )}
    </div>
  );

  /**
   * A FIXED box, whatever is in it.
   *
   * It grew and shrank with whatever was being read — a one-line hint, then a kind label over three
   * lines of a teacher's paragraph — and everything above it moved each time. Stepping through a
   * task became a strip that jumped on every press, with the task below it sliding to match, which
   * is the worst possible behaviour for the surface you are trying to read along with.
   *
   * Two lines' worth, and longer rows scroll inside it. `informative` — the "Play from 7" caption —
   * lives in here for the same reason: as a row of its own it appeared and vanished, and this box is
   * already reserved space.
   */
  const transcript = (
    <div className='h-12 overflow-y-auto'>
      {current ? (
        <>
          <p className='mb-0.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-ws-muted'>
            <KindIcon row={current} />
            {shown !== null && `${shown + 1}. `}{kindLabel(current, t) ?? ''}
          </p>
          <p className='text-xs text-ws-text'>
            {current.name || <span className='italic text-ws-warning-fg'>{t('task.bottomBar.a11yReadNoAlt')}</span>}
          </p>
        </>
      ) : (
        <p className='text-[11px] italic text-ws-muted'>{t('task.bottomBar.a11yReadTapBadge')}</p>
      )}
    </div>
  );

  /**
   * INLINE, above the task — not a bar floating over it.
   *
   * Both bars used to be `fixed`, pinned to the task's top and bottom edges, and the top one sat on
   * the first thing in the task: the one element you could never read was the one the read-through
   * started with. In the column it pushes the task down instead, which costs about a hundred pixels
   * of an 844px screen and makes all of it legible.
   *
   * Transport and transcript stack together here, the way the landscape rail already stacks them,
   * so the two orientations are the same idea at two widths rather than two different frames.
   */
  return (
    <>
      {/* Tapping a number PLAYS it, rather than merely selecting it.
          On a desktop a badge selects, because the list is right there with a play button per row.
          Here the list is not on screen at all, so selecting is a press that appears to do nothing
          and leaves a second press to find. A badge on a phone is the play button. */}
      <ReadThroughOverlay
        rows={rows}
        getRoot={getRoot}
        activeIndex={shown}
        onPick={(i) => { onPick(i); play([i]); }}
      />
      <div className='shrink-0 space-y-1 border-b border-ws-border bg-ws-surface px-3 py-2'>
        {/* Transport left, scope right — the same row, in the same order, as the docked panel. */}
        <div className='flex items-center gap-2'>
          {transport}
          {/* The scope switch was desktop-only, so a teacher on a phone could read their own content
              aloud and had no way to hear the controls around it — the half of the review that
              answers "can this be operated", not just "does this make sense". */}
          <Switch
            on={full}
            onChange={onFull}
            label={`${t('task.bottomBar.a11yScopeAuthored')} / ${t('task.bottomBar.a11yScopeFull')}`}
            /* SHORT on the chips, long in the label. The visible pair only has to distinguish two
               positions on a control a teacher is looking straight at; the accessible name has to
               stand on its own, because a listener meets it with no switch in front of them. The
               `states` are `aria-hidden` for exactly this reason, so the two can differ. */
            states={{ off: t('task.bottomBar.a11yScopeAuthoredShort'), on: t('task.bottomBar.a11yScopeFullShort') }}
            className='min-w-0 flex-1'
          />
        </div>
        {transcript}
      </div>
    </>
  );
}

/**
 * How far off an element's top-left corner a badge sits.
 *
 * Enough that the number frames the content rather than covering it, then floored at 0 on both
 * axes: the layer's box IS the content's box, so a negative offset pushes past the scroll
 * container's own edge, where it is clipped — which is exactly the "clipped on the left and top"
 * that started this.
 *
 * Note what this floor is NOT. It is a CONTENT coordinate, so it only ever affects the handful of
 * elements flush against the content's own top-left corner, and it cannot move. The clamp it
 * replaces was in viewport coordinates against a frame that shifted on every scroll event, which is
 * what made badges pile up along an edge on their way out of view.
 *
 * Nothing clamps the far side any more: a badge below the fold scrolls out with the thing it
 * labels, which is what it should always have done.
 */
const BADGE_NUDGE = 9;

/** Badge side, in px. The de-collision test needs it as a number; the class below draws it. */
const BADGE_PX = 18;

/** `null` for a row that has no place on the task's map — see the guard in `useBadgeOffsets`. */
type Offset = { top: number; left: number } | null;

function ReadThroughOverlay({ rows, getRoot, activeIndex, onPick }: {
  rows: OutlineRow[];
  /** The task. Resolved at measure time, never captured — it re-renders on submit and reset. */
  getRoot: () => Element | null;
  activeIndex: number | null;
  /** What a badge does. Selecting the row on desktop, where the list is right there with its own
   * play button; PLAYING it on a collapsed phone, where the list is not on screen at all. */
  onPick: (index: number) => void;
}) {
  const { layer, offsets } = useBadgeOffsets(rows, getRoot);

  if (!layer) return null;

  /**
   * Portalled INTO the task's own content, not onto `document.body`.
   *
   * That single change is what retires all the coordination this used to need. Badges scroll with
   * the content because they are in it; the scroll container's own `overflow` clips them, so there
   * is no hand-drawn clip box and no way for one to land on the bottom bar; and the stacking is
   * local, because the transport and the bottom bar are `fixed` elements outside the task and share
   * no stacking context with this.
   *
   * The LAYER ignores the pointer so the task underneath stays usable; each badge takes it back.
   */
  return createPortal(
    <>
      {offsets.map((o, i) => (o == null ? null : (
        <button
          key={i}
          type='button'
          tabIndex={-1}
          aria-hidden='true'
          onClick={() => onPick(i)}
          style={{ top: Math.max(0, o.top - BADGE_NUDGE), left: Math.max(0, o.left - BADGE_NUDGE) }}
          className={`pointer-events-auto absolute flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold tabular-nums shadow-sm ring-1 ring-black/10 ${
            activeIndex === i ? 'bg-ws-primary text-white' : 'bg-ws-warning-bg text-ws-warning-fg'}`}
        >
          {i + 1}
        </button>
      )))}
    </>,
    layer,
  );
}

/**
 * Remembered across sessions — see the note where it is read.
 *
 * The name lags the label, which now reads "With Interactives". Deliberately: renaming the key
 * would silently drop the preference of everyone who has already set it, and a stored key is an
 * identifier rather than a caption.
 */
const FULL_SCOPE_KEY = 'agavi.a11yEmulator.fullExperience';

/**
 * ONE panel now, where there were two.
 *
 * The other was PROBLEMS — a list of faults to fix. Every one it reported (missing alt text, a
 * missing motion description, a missing transcript) is already an error on the block's own card,
 * beside the field that fixes it, and gates publication from there. The only finding unique to it
 * was contrast, which has been retired outright: see `preview-a11y-scan`.
 *
 * What is left is an INSPECTION — how the task is perceived — which is interesting rather than
 * actionable, and was always the half that could not be got anywhere else.
 */
export function PreviewA11yPanel({ getRoot, getScanRoot, scanKey, onClose, fullScreen, phoneFrame, compact }: {
  /** Resolved at scan time, not passed as a node: the task re-renders on submit and reset, so a
   * captured element would go stale and the report would describe a detached tree. */
  getRoot: () => Element | null;
  /**
   * What to READ, where that is wider than the task: the results bar and Submit beneath it.
   *
   * Separate from `getRoot`, which stays the task's own box because three other things depend on
   * it meaning exactly that — the badge layer lives inside the task's scrolled content, and the
   * phone frame's bars are anchored to the task's edges precisely so they leave Preview's own
   * controls alone. Widening that would put the transcript bar under Submit.
   *
   * A student meets the bars, so the review should read them. Defaults to `getRoot`, so a caller
   * with nothing extra to scan says nothing.
   */
  getScanRoot?: () => Element | null;
  /** Changes whenever the previewed task has re-rendered. Re-scanning is keyed on it. */
  scanKey: string;
  onClose: () => void;
  fullScreen?: boolean;
  /** Phone only: reading mode renders as a frame around the task rather than a sheet over it. */
  phoneFrame?: boolean;
  /** Docked in a narrow, short space — the phone-landscape rail. Sheds the standing disclaimer. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [outline, setOutline] = useState<OutlineRow[]>([]);
  /** Shared by the list and the overlay, so selecting in one marks the other. */
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  /**
   * Whether the emulator includes our controls as well as the teacher's own content.
   *
   * Persisted, because it is a way of WORKING rather than a per-visit choice: an accessibility
   * tester lives in the full scope and had to switch back to it every time the panel closed.
   * `localStorage` and not page state for the same reason — closing the panel is not the only way
   * to lose it. Guarded, since a private window can refuse both the read and the write.
   */
  const [full, setFull] = useState(() => {
    try { return localStorage.getItem(FULL_SCOPE_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(FULL_SCOPE_KEY, full ? '1' : '0'); } catch { /* refused; the session still works */ }
  }, [full]);

  const scan = useCallback(() => {
    // The WIDER root where there is one: a student meets the results bar and Submit, so the
    // read-through reads them.
    const root = (getScanRoot ?? getRoot)();
    if (!root) return;
    /**
     * Nothing is skipped here for being hidden — `isHidden` inside the scan does that per element,
     * and it is load-bearing: while Preview's submission summary is up, the task and its bar are
     * `aria-hidden` and the dialog is not, so one scan of this root reports the dialog and nothing
     * else. Which is what a real reader does, and what the review should therefore say.
     */
    setOutline(screenReaderOutline(root));
  }, [getRoot, getScanRoot]);

  /**
   * Re-scans when the task re-renders.
   *
   * There used to be a Re-scan button here, which was the wrong shape for the problem: a teacher
   * never knows the moment to press it, and the one thing that most changes the findings —
   * submitting — nobody would connect to a refresh icon. Feedback bodies are authored rich text that
   * only enter the DOM on submit, so before this they went unreported unless someone pressed the
   * button on a hunch.
   *
   * This covers the four page-level events `scanKey` names. Everything a teacher does INSIDE the
   * task is covered by `useLiveScan` below, which is the other half and was missing.
   */
  useEffect(() => {
    const id = window.setTimeout(scan, 120);
    return () => window.clearTimeout(id);
  }, [scan, scanKey]);

  /**
   * AUTHORED only, and MEMOISED — the second part is not a nicety.
   *
   * Rebuilt inline, this array had a new identity on every render, and playback moves the highlight
   * through React state. So speaking item one re-rendered the panel, which produced a new array,
   * which fired an effect keyed on its identity, which called `cancel()` — playback stopped itself
   * after the first item. Memoising it, and keying the stop on the scan revision instead, are the
   * two halves of that fix.
   *
   * Our controls and containers are excluded: reading order is not something a teacher can break in
   * a vertical stack of blocks, and an inline control is spoken inside its block's flow anyway. An
   * image with no alt keeps its place, because silence there is the thing worth hearing.
   */
  /**
   * AUTHORED only by default, EVERYTHING on request.
   *
   * Two different jobs. A teacher reviewing their own words does not want thirty rows of our
   * buttons between their sentences — nothing there is theirs to change. But hearing the task the
   * way a student tabs through it is the only way to know whether it works at all, and it is what
   * an accessibility tester needs in order to tell us we built the controls wrong.
   *
   * Everything means everything a STUDENT meets, which is wider than the task's own box: the
   * results bar and Submit are in, because a student meets those. What is out is what the student
   * runner will not render — Reroll and the phone-sensor simulator, marked `data-teacher-only` at
   * the point they are written and dropped by the scan itself.
   *
   * It also includes our own static text, not just our controls. A reader does not only tab, it
   * reads, and in the sensor blocks the prose between the controls is the load-bearing part: GPS
   * states the tolerance it grades on and shows the live coordinate as running text.
   */
  const reading = useMemo(() => {
    // `!r.inline`: a control that sits inside an authored sentence was already spoken there, in the
    // place a listener meets it. A second row for it read every FITB blank twice.
    const rows = outline.filter(r =>
      (full ? !r.inline : r.authored) && (r.name || r.kind === 'image' || r.kind === 'control' || r.kind === 'table'));
    /**
     * The role and state are what WITH INTERACTIVES adds; they are not the row.
     *
     * An MCQ option is a control whose name is the teacher's own text, so it belongs in both
     * scopes — but reading "10 m/s, checkbox, not checked" under Your Content answers a question
     * that scope is not asking. Your Content is "does what I wrote make sense heard"; the widget
     * around the words is the other scope's business.
     */
    // The role and state are the only full-scope dressing; everything else a row says, it says in
    // both scopes, because it is what a reader would actually be given.
    return rows.map((r): ReadingRow => {
      if (full && r.kind === 'control') return { ...r, name: controlSpeech(r, t) };
      const named = r.kind === 'table' ? { ...r, name: tableSummary(r, t) } : r;
      return { ...named, speech: spokenWithKind(named, t) };
    });
  }, [outline, full, t]);
  const speech = useReadAloud(reading, scanKey, setActiveIndex);
  /**
   * A NEW SCAN CLEARS THE SELECTION, and that is what makes Play work on the next task.
   *
   * `useReadAloud` already stops playback when `scanKey` changes, but the selected ROW survived —
   * so stepping to another task in Preview left the cursor on, say, row 12 of the task you had
   * left. `play()` starts `from = activeIndex ?? 0`, and on a shorter task row 12 is past the end:
   * the queue was empty and pressing Play spoke nothing at all, with no sign of why.
   *
   * Reported as "flip to another page in preview and the screen reader doesn't play anything".
   *
   * Clearing rather than clamping, because even where the index is still in range it now points at
   * unrelated content — the row numbers belong to the task that produced them.
   */
  useEffect(() => { setActiveIndex(null); }, [scanKey]);
  /**
   * And again whenever the ROWS shrink under it.
   *
   * `scanKey` names the page-level events; the live scan changes rows without touching it — a
   * teacher clearing an answer removes the feedback row it had produced. Same dead Play button,
   * reached without ever leaving the task, so the guard is on both signals rather than the one
   * that happened to be reported.
   */
  useEffect(() => {
    setActiveIndex(i => (i !== null && i >= reading.length ? null : i));
  }, [reading.length]);
  // The other half of the scan schedule: everything a teacher does INSIDE the task, which `scanKey`
  // cannot see. Held off while a voice is reading; see the hook.
  useLiveScan(getScanRoot ?? getRoot, scan, speech.speakingIndex !== null);
  const rowRefs = useRef<(HTMLLIElement | null)[]>([]);
  /** Read through a ref so selection callbacks stay stable while still seeing live playback state. */
  const speechRef = useRef<ReturnType<typeof useReadAloud> | null>(null);
  speechRef.current = speech;
  /* The task sits beside the panel on desktop and BEHIND a frame, not a sheet, on a phone — so
     nothing has to move out of the way before revealing. It did once: the problem list was a
     full-screen sheet there, and "Show me" had to hand the screen over first. That sheet is gone
     and `onListen` with it. */
  const revealOnTask = useCallback((el: Element) => { revealElement(el); }, []);
  /**
   * Selecting on the task brings the row to you.
   *
   * Pressing a badge marked the row and left it wherever it was in a scrolled list, so on a long
   * task the answer to "what does that one say" was off-screen. `block: 'nearest'` because the row
   * is usually already visible and yanking the list to centre it every time is worse.
   */
  /**
   * Choosing a card while PAUSED abandons the held position and re-arms from the new one.
   *
   * Otherwise the transport still said Resume, and pressing it carried on from wherever the pause
   * happened — so selecting a card mid-pause looked like it had done nothing. Stopping is what lets
   * the button become "Play from 7", which is what the selection was asking for.
   */
  /**
   * The list follows what is being spoken.
   *
   * `highlight` already brings the TASK element into view, but the panel's own list sat still — so
   * on a long task the row being read was somewhere off-screen while its element scrolled past.
   * `block: 'nearest'` because the row is usually already visible, and yanking the list to centre
   * it on every utterance would be worse than not following at all.
   */
  useEffect(() => {
    if (speech.speakingIndex === null) return;
    rowRefs.current[speech.speakingIndex]?.scrollIntoView?.({ block: 'nearest' });
  }, [speech.speakingIndex]);

  const selectRow = useCallback((i: number) => {
    setActiveIndex(i);
    const s = speechRef.current;
    if (!s) return;
    /**
     * Mid-listen, a click means "that one, now" — the same thing the skip buttons mean.
     *
     * Selecting used to only move the marker, so clicking a card while the task was being read
     * aloud changed what Play WOULD do next and nothing about what was being said. Going there is
     * the only reason to click a card during playback.
     *
     * Paused stays as it was: the queue is being held deliberately, and jumping elsewhere abandons
     * that position, so the transport re-arms as "Play from N" rather than un-pausing somewhere
     * new. The same rule the skip buttons follow.
     */
    if (s.speakingIndex !== null && !s.paused) s.playFrom(i);
    else if (s.paused) s.stop();
  }, []);

  const selectFromTask = useCallback((i: number) => {
    selectRow(i);
    rowRefs.current[i]?.scrollIntoView?.({ block: 'nearest' });
  }, [selectRow]);

  /**
   * Handed over to the task: badges and a bar, nothing else.
   *
   * A 390px phone cannot show a full-screen list and the task it describes at once, and the two
   * jobs genuinely differ. Listening through without seeing is useful in itself — it is the closest
   * thing here to receiving the task rather than reading it. But wanting to hear ONE element while
   * looking at it is the other half, and with the sheet up there is nothing on screen to tap. So
   * the sheet steps aside and every badge becomes its own play button.
   */
  /**
   * Handed over to the task: the bar, and nothing else.
   *
   * A 390px phone cannot show a full-screen review and the task it describes at once, and BOTH
   * modes need to cross that gap. Reading wants it because listening through blind and hearing one
   * element while looking at it are different jobs. Problems wants it because "Show me" was
   * revealing an element behind the sheet — scrolling and flashing something the teacher could not
   * see, with closing the review the only way to look, which lost their place.
   *
   * So the sheet steps aside rather than closing, and the bar is the way back. In reading mode the
   * badges come with it and each one plays its own part.
   */
  /**
   * Reading on a phone is a FRAME around the task, never a sheet over it.
   *
   * A full-screen list answered the wrong question here: what a teacher wants is "what does THAT
   * part sound like", and with a list covering the task that meant closing it, finding the part,
   * and opening it again. See `PhoneReadFrame`.
   */
  if (phoneFrame) {
    return (
      <PhoneReadFrame
        rows={reading}
        getRoot={getRoot}
        speech={speech}
        activeIndex={activeIndex}
        onPick={selectRow}
        full={full}
        onFull={setFull}
      />
    );
  }

  const heading = t('task.bottomBar.a11yReadingHeading');

  /**
   * On a phone this is a full-screen sheet, so it gets the app's SHEET titlebar.
   *
   * The same coloured bar Flow and Variables use on a phone — tinted band, a symbol at 24px, the
   * title, and the close on the right. A sheet IS the whole screen there, with no toolbar beside it
   * to borrow context from, and a bare line of small text at the top read as an unfinished view
   * rather than as the thing you had just pressed.
   *
   * The app's own tint, not Flow's. There was a second, warning-coloured variant for the problem
   * list; that panel is gone — every fault it reported is an error on the block's own card, beside
   * the field that fixes it — and with it the one thing this header had to vary.
   *
   * Docked on desktop, it stays the plain small header it was: the panel is a bordered card beside
   * the task there, and a full-width colour band would give a side panel the weight of a page.
   */
  const header = fullScreen ? (
    <div className='flex shrink-0 items-center gap-2 border-b border-ws-primary/20 bg-ws-primary-subtle px-4 py-2.5 text-base font-semibold text-ws-primary dark:bg-ws-text-link/[.20] dark:text-ws-text-link'>
      <Ear aria-hidden='true' className='h-6 w-6' />
      <span className='flex-1'>{heading}</span>
      <button
        type='button' onClick={onClose} aria-label={t('task.bottomBar.a11yReviewClose')}
        className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10'
      >
        <X aria-hidden='true' className='h-5 w-5' />
      </button>
    </div>
  ) : (
    <div className='flex shrink-0 items-center gap-2'>
      <h2 className='flex-1 text-sm font-semibold text-ws-text'>{heading}</h2>
      <button
        type='button' onClick={onClose} aria-label={t('task.bottomBar.a11yReviewClose')}
        className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-ws-hover'
      >
        <X aria-hidden='true' className='h-4 w-4' />
      </button>
    </div>
  );

  return (
    <div className={`flex min-h-0 w-full flex-col ${fullScreen ? 'h-full' : 'h-full gap-3 overflow-hidden rounded-xl border border-ws-border bg-ws-surface p-3'}`}>
      {header}
      {/* The sheet's titlebar is full-bleed, so the padding that used to sit on the container moves
          in here, below it. `contents` on desktop leaves the existing single-column flow untouched. */}
      <div className={fullScreen ? 'flex min-h-0 flex-1 flex-col gap-3 p-4' : 'contents'}>

      <ReadThroughControls
        rows={reading}
        speech={speech}
        activeIndex={activeIndex}
        full={full}
        onFull={setFull}
        onStep={(i) => { selectRow(i); rowRefs.current[i]?.scrollIntoView?.({ block: 'nearest' }); revealElement(reading[i]!.el); }}
        compact={compact}
      />

      {/* `pr-2` so the cards do not butt against the scrollbar. The padding belongs on the
          SCROLLER rather than the list inside it: put it on the list and it scrolls away with the
          content, leaving the gap only where there is content to hold it open. */}
      <div className='min-h-0 flex-1 space-y-4 overflow-y-auto pr-2'>
        <ReadThroughList
          rows={reading}
          speech={speech}
          activeIndex={activeIndex}
          onPick={selectRow}
          onReveal={revealOnTask}
          rowRefs={rowRefs}
        />
      </div>

      {reading.length > 0 && (
        <ReadThroughOverlay
          rows={reading}
          getRoot={getRoot}
          activeIndex={activeIndex}
          onPick={(i) => { selectFromTask(i); revealElement(reading[i]!.el); }}
        />
      )}
      </div>
    </div>
  );
}
